import React, { useRef, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  CircularProgress,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Business,
  LocationOn,
  AttachMoney,
  Description,
  Photo,
  SquareFoot,
  Bathtub,
  KingBed,
} from '@mui/icons-material';
import api from '../services/api';
import AddressAutocompleteField from '../components/AddressAutocompleteField';

export default function AddPropertyModal({ open, onClose, onPropertyAdded, vendorId }) {
  const MAX_UPLOAD_BYTES = 10 * 1024 * 1024; // 10MB (images + disclosures)
  const MAX_IMAGE_COUNT = 20;

  const parseMoney = (value) => {
    const raw = (value ?? '').toString().trim();
    const normalized = raw.replace(/[$,\s]/g, '');
    const n = Number(normalized);
    return Number.isFinite(n) ? n : NaN;
  };

  const parseHttpUrl = (input) => {
    const raw = (input ?? '').toString().trim();
    if (!raw) return null;
    const candidate = raw.includes('://') ? raw : `https://${raw}`;
    try {
      const u = new URL(candidate);
      if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
      if (u.hostname !== 'localhost' && !u.hostname.includes('.')) return null;
      return u;
    } catch {
      return null;
    }
  };

  const normalizeHttpUrl = (input) => {
    const u = parseHttpUrl(input);
    return u ? u.toString() : '';
  };

  const [formData, setFormData] = useState({
    address: '',
    city: '',
    state: '',
    zipCode: '',
    propertyType: '',
    bedrooms: '',
    bathrooms: '',
    squareFootage: '',
    lotSize: '',
    lotSizeUnit: 'sqft',
    askingPrice: '',
    hoaFee: '',
    spaceRent: '',
    closeOfEscrow: '',
    conventionalFinancingAvailable: false,
    status: 'available',
    visibility: 'public',
    description: '',
    features: [],
    uploadedImages: [],
    externalImageUrls: [],
    igGuideUrl: '',
    uploadedDisclosures: [],
    // Places / geocoding metadata
    latitude: null,
    longitude: null,
    placeId: null,
    formattedAddress: null,
  });

  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [loading, setLoading] = useState(false);
  const disclosuresUploadsInFlightRef = useRef(0);
  const [disclosuresUploading, setDisclosuresUploading] = useState(false);
  const [categoriesData, setCategoriesData] = useState({
    propertyTypes: [],
    locations: [],
    features: []
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.categories.getAll();
        setCategoriesData(response.data);
      } catch (error) {
        console.error('Error fetching categories:', error);
        // Fallback to hardcoded data if API fails
        setCategoriesData({
          propertyTypes: [
            { id: 'single-family', name: 'Single Family Home' },
            { id: 'multi-family', name: 'Multi-Family Home' },
            { id: 'condo', name: 'Condo' },
            { id: 'townhouse', name: 'Townhouse' },
            { id: 'duplex', name: 'Duplex' },
            { id: 'commercial', name: 'Commercial' },
            { id: 'land', name: 'Land' },
            { id: 'other', name: 'Other' }
          ],
          locations: [],
          features: []
        });
      }
    };

    fetchCategories();
  }, []);

  const propertyTypes = categoriesData.propertyTypes;

  const errorBoxRef = useRef(null);

  const scrollToErrorBox = () => {
    // Wait for the error box to render before scrolling.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (errorBoxRef.current && errorBoxRef.current.scrollIntoView) {
          errorBoxRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  };

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const tooLarge = files.filter((f) => (f?.size || 0) > MAX_UPLOAD_BYTES);
    if (tooLarge.length > 0) {
      setSubmitError(`Images must be 10MB or less. Please remove: ${tooLarge.map((f) => f.name).join(', ')}`);
      scrollToErrorBox();
      try { event.target.value = ''; } catch {}
      return;
    }

    const existingCount = formData.uploadedImages?.length || 0;
    const remaining = Math.max(0, MAX_IMAGE_COUNT - existingCount);
    if (remaining <= 0) {
      setSubmitError(`You can upload up to ${MAX_IMAGE_COUNT} images.`);
      scrollToErrorBox();
      try { event.target.value = ''; } catch {}
      return;
    }

    const accepted = files.slice(0, remaining);
    if (accepted.length < files.length) {
      setSubmitError(`Only the first ${remaining} image(s) were added (max ${MAX_IMAGE_COUNT} total).`);
      scrollToErrorBox();
    }

    const imageFiles = accepted.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setFormData((prev) => ({
      ...prev,
      uploadedImages: [...(prev.uploadedImages || []), ...imageFiles],
    }));
    try { event.target.value = ''; } catch {}
  };

  const handleDisclosureUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const tooLarge = files.filter((f) => (f?.size || 0) > MAX_UPLOAD_BYTES);
    if (tooLarge.length > 0) {
      setSubmitError(`Disclosure files must be 10MB or less. Please remove: ${tooLarge.map((f) => f.name).join(', ')}`);
      scrollToErrorBox();
      // Clear the input so user can re-select.
      try { event.target.value = ''; } catch {}
      return;
    }

    disclosuresUploadsInFlightRef.current += 1;
    setDisclosuresUploading(true);
    try {
      // Upload files to Firebase Storage
      const uploadPath = `properties/${vendorId || 'temp'}/disclosures`;
      const uploadResult = await api.storage.uploadFiles(files, uploadPath);

      // Store the uploaded file information
      const disclosureFiles = uploadResult.data.map(upload => ({
        name: upload.name,
        size: upload.size,
        type: upload.type,
        downloadURL: upload.downloadURL,
        path: upload.path,
        uploadedAt: upload.uploadedAt
      }));

      setFormData(prev => ({
        ...prev,
        uploadedDisclosures: [...prev.uploadedDisclosures, ...disclosureFiles]
      }));
    } catch (error) {
      console.error('Error uploading disclosure files:', error);
      // For now, store as local files if upload fails (fallback)
      const disclosureFiles = files.map(file => ({
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        uploadedAt: new Date().toISOString(),
        uploadError: true
      }));
      setFormData(prev => ({
        ...prev,
        uploadedDisclosures: [...prev.uploadedDisclosures, ...disclosureFiles]
      }));
    } finally {
      disclosuresUploadsInFlightRef.current = Math.max(0, disclosuresUploadsInFlightRef.current - 1);
      setDisclosuresUploading(disclosuresUploadsInFlightRef.current > 0);
    }
  };


  const handleRemoveDisclosure = async (index) => {
    const disclosure = formData.uploadedDisclosures[index];

    // If the file has a path (meaning it was uploaded to storage), delete it from storage
    if (disclosure.path) {
      try {
        await api.storage.deleteFile(disclosure.path);
      } catch (error) {
        console.error('Error deleting disclosure file from storage:', error);
        // Continue with removing from form even if storage deletion fails
      }
    }

    setFormData(prev => ({
      ...prev,
      uploadedDisclosures: prev.uploadedDisclosures.filter((_, i) => i !== index)
    }));
  };


  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
      // If user manually edits address parts, clear Places-derived metadata so we can re-geocode.
      ...(field === 'address' || field === 'city' || field === 'state' || field === 'zipCode'
        ? { latitude: null, longitude: null, placeId: null, formattedAddress: null }
        : {})
    }));
    if (submitError) {
      setSubmitError(null);
    }
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.address.trim()) newErrors.address = 'Street address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!formData.zipCode.trim()) newErrors.zipCode = 'ZIP code is required';
    if (!formData.propertyType) newErrors.propertyType = 'Property type is required';
    if (!formData.askingPrice || !Number.isFinite(parseMoney(formData.askingPrice))) {
      newErrors.askingPrice = 'Valid asking price is required';
    }
    if (!formData.description.trim()) newErrors.description = 'Description is required';

    const externalUrlRaw = formData.externalImageUrls?.[0] || '';
    if (externalUrlRaw) {
      const normalized = normalizeHttpUrl(externalUrlRaw);
      if (!normalized) {
        newErrors.externalImageUrl = 'Please enter a valid URL (https://...)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (disclosuresUploadsInFlightRef.current > 0) {
      setSubmitError('Please wait for disclosure uploads to finish before saving.');
      scrollToErrorBox();
      return;
    }
    if (!validateForm()) {
      setSubmitError('Please fix the highlighted fields below.');
      scrollToErrorBox();
      return;
    }

    setLoading(true);
    try {
      setSubmitError(null);
      // Prefer Places coordinates if available; otherwise geocode server-side.
      let latitude = formData.latitude;
      let longitude = formData.longitude;
      let placeId = formData.placeId;
      let formattedAddress = formData.formattedAddress;

      if (latitude == null || longitude == null) {
        console.log('Geocoding address...');
        const geocodeResult = await api.geocoding.geocodeAddress(
          formData.address,
          formData.city,
          formData.state,
          formData.zipCode
        );
        console.log('Geocode result:', geocodeResult);
        latitude = geocodeResult?.data?.latitude ?? null;
        longitude = geocodeResult?.data?.longitude ?? null;
        placeId = placeId || geocodeResult?.data?.placeId || null;
        formattedAddress = formattedAddress || geocodeResult?.data?.formattedAddress || null;
      }

      // Process disclosures - remove any file objects and keep only uploaded file info
      const disclosures = (formData.uploadedDisclosures || [])
        .filter(disclosure => !disclosure.file && !disclosure.uploadError) // Only include successfully uploaded files
        .map(disclosure => ({
          name: disclosure.name,
          size: disclosure.size,
          type: disclosure.type,
          downloadURL: disclosure.downloadURL,
          path: disclosure.path,
          uploadedAt: disclosure.uploadedAt
        }));

      const {
        uploadedImages,
        uploadedDisclosures,
        askingPrice,
        ...restFormData
      } = formData;

      // Upload any newly selected images (local files) to Storage and store download URLs.
      const imageFiles = (formData.uploadedImages || [])
        .map((img) => img && img.file ? img.file : null)
        .filter(Boolean);

      let uploadedImageUrls = [];
      if (imageFiles.length > 0) {
        const uploadPath = `properties/${vendorId || 'temp'}/images`;
        const uploadResult = await api.storage.uploadFiles(imageFiles, uploadPath);
        uploadedImageUrls = (uploadResult?.data || [])
          .map((u) => u && u.downloadURL ? u.downloadURL : null)
          .filter(Boolean);
      }

      const externalImageUrlNormalized = normalizeHttpUrl(formData.externalImageUrls?.[0] || '');
      const externalImageUrls = externalImageUrlNormalized ? [externalImageUrlNormalized] : [];

      const images = [
        ...uploadedImageUrls,
        ...externalImageUrls,
      ].filter(Boolean);

      const propertyData = {
        ...restFormData,
        vendorId: vendorId,
        disclosures: disclosures,
        images: images,
        externalImageUrls: externalImageUrls,
        // Convert string numbers to actual numbers
        bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
        bathrooms: formData.bathrooms ? parseFloat(formData.bathrooms) : null,
        squareFootage: formData.squareFootage ? parseInt(formData.squareFootage) : null,
        lotSize: formData.lotSize ? parseFloat(formData.lotSize) : null,
        // Canonical field used across the app UI
        price: parseMoney(formData.askingPrice),
        hoaFee: formData.hoaFee ? parseInt(formData.hoaFee) : null,
        spaceRent: formData.spaceRent ? parseInt(formData.spaceRent) : null,
        closeOfEscrow: formData.closeOfEscrow || null,
      };

      // Add coordinates if available (from Places or geocoding)
      if (latitude !== null && longitude !== null) {
        propertyData.latitude = latitude;
        propertyData.longitude = longitude;
      }
      if (formattedAddress) {
        propertyData.formattedAddress = formattedAddress;
      }
      if (placeId) {
        propertyData.placeId = placeId;
      }

      console.log('Creating property with data:', propertyData);
      await api.properties.create(propertyData);
      resetForm();
      onClose();
      if (onPropertyAdded) {
        onPropertyAdded();
      }
    } catch (error) {
      console.error('Error creating property:', error);
      setSubmitError(error?.message || 'Failed to create property. Please try again.');
      scrollToErrorBox();
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    // Clean up any object URLs to prevent memory leaks
    formData.uploadedImages.forEach(image => {
      URL.revokeObjectURL(image.preview);
    });

    setFormData({
      address: '',
      city: '',
      state: '',
      zipCode: '',
      propertyType: '',
      bedrooms: '',
      bathrooms: '',
      squareFootage: '',
      lotSize: '',
      lotSizeUnit: 'sqft',
      askingPrice: '',
      hoaFee: '',
      spaceRent: '',
      closeOfEscrow: '',
      conventionalFinancingAvailable: false,
      status: 'available',
      visibility: 'public',
      description: '',
      features: [],
      uploadedImages: [],
      externalImageUrls: [],
      igGuideUrl: '',
      uploadedDisclosures: [],
      latitude: null,
      longitude: null,
      placeId: null,
      formattedAddress: null,
    });
    setErrors({});
    setSubmitError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ color: '#008080', display: 'flex', alignItems: 'center' }}>
        <Business sx={{ mr: 1 }} />
        Add New Property
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {(submitError || Object.keys(errors).length > 0) && (
            <Box
              ref={errorBoxRef}
              tabIndex={-1}
              sx={{
                mb: 2,
                p: 2,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'error.light',
                backgroundColor: 'rgba(211, 47, 47, 0.06)'
              }}
            >
              <Typography variant="subtitle2" color="error" sx={{ mb: 0.5 }}>
                Please review the errors below
              </Typography>
              {submitError && (
                <Typography variant="body2" color="error">
                  {submitError}
                </Typography>
              )}
            </Box>
          )}

          {/* Location Section */}
          <Typography variant="h6" gutterBottom sx={{ color: '#008080', mt: 2, display: 'flex', alignItems: 'center' }}>
            <LocationOn sx={{ mr: 1, verticalAlign: 'middle' }} />
            Location Details
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <AddressAutocompleteField
                label="Search address *"
                value={''}
                onChange={() => {}}
                onPlaceSelected={(parsed) => {
                  setFormData((prev) => ({
                    ...prev,
                    address: parsed.address || prev.address,
                    city: parsed.city || prev.city,
                    state: parsed.state || prev.state,
                    zipCode: parsed.zipCode || prev.zipCode,
                    latitude: parsed.latitude ?? null,
                    longitude: parsed.longitude ?? null,
                    placeId: parsed.placeId || null,
                    formattedAddress: parsed.formattedAddress || null,
                  }));
                }}
                helperText="Optional: start typing and select a suggestion to auto-fill"
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street Address *"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                error={!!errors.address}
                helperText={errors.address}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City *"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                error={!!errors.city}
                helperText={errors.city}
                required
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="State *"
                value={formData.state}
                onChange={(e) => handleInputChange('state', e.target.value)}
                error={!!errors.state}
                helperText={errors.state}
                required
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="ZIP Code *"
                value={formData.zipCode}
                onChange={(e) => handleInputChange('zipCode', e.target.value)}
                error={!!errors.zipCode}
                helperText={errors.zipCode}
                required
              />
            </Grid>
          </Grid>

          {/* Property Details Section */}
          <Typography variant="h6" gutterBottom sx={{ color: '#008080', mt: 4, display: 'flex', alignItems: 'center' }}>
            <Business sx={{ mr: 1, verticalAlign: 'middle' }} />
            Property Details
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.propertyType}>
                <InputLabel>Property Type *</InputLabel>
                <Select
                  value={formData.propertyType}
                  label="Property Type *"
                  onChange={(e) => handleInputChange('propertyType', e.target.value)}
                >
                  {propertyTypes.map((type) => {
                    const typeName = typeof type === 'string' ? type : type.name;
                    const typeValue = typeof type === 'string' ? type : type.id;
                    return (
                      <MenuItem key={typeValue} value={typeValue}>
                        {typeName}
                      </MenuItem>
                    );
                  })}
                </Select>
                {errors.propertyType && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                    {errors.propertyType}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => handleInputChange('status', e.target.value)}
                >
                  <MenuItem value="available">Available</MenuItem>
                  <MenuItem value="on-hold">On-Hold</MenuItem>
                  <MenuItem value="first come first serve">First Come First Serve</MenuItem>
                  <MenuItem value="vacant on lockbox">Vacant on Lockbox</MenuItem>
                  <MenuItem value="under contract">Under Contract</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="sold">Sold</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Visibility</InputLabel>
                <Select
                  value={formData.visibility}
                  label="Visibility"
                  onChange={(e) => handleInputChange('visibility', e.target.value)}
                >
                  <MenuItem value="public">Public</MenuItem>
                  <MenuItem value="private">Private</MenuItem>
                  <MenuItem value="draft">Draft</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Bedrooms"
                type="number"
                value={formData.bedrooms}
                onChange={(e) => handleInputChange('bedrooms', e.target.value)}
                error={!!errors.bedrooms}
                helperText={errors.bedrooms}
                InputProps={{
                  startAdornment: <KingBed sx={{ mr: 1, color: 'action.disabled' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Bathrooms"
                type="number"
                step="0.5"
                value={formData.bathrooms}
                onChange={(e) => handleInputChange('bathrooms', e.target.value)}
                error={!!errors.bathrooms}
                helperText={errors.bathrooms}
                InputProps={{
                  startAdornment: <Bathtub sx={{ mr: 1, color: 'action.disabled' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Square Footage"
                type="number"
                value={formData.squareFootage}
                onChange={(e) => handleInputChange('squareFootage', e.target.value)}
                error={!!errors.squareFootage}
                helperText={errors.squareFootage}
                InputProps={{
                  startAdornment: <SquareFoot sx={{ mr: 1, color: 'action.disabled' }} />,
                }}
                placeholder="e.g., 2000"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Lot Size"
                type="number"
                step="0.01"
                value={formData.lotSize}
                onChange={(e) => handleInputChange('lotSize', e.target.value)}
                error={!!errors.lotSize}
                helperText={errors.lotSize}
                placeholder="e.g., 0.25"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Lot Size Unit</InputLabel>
                <Select
                  value={formData.lotSizeUnit || 'sqft'}
                  label="Lot Size Unit"
                  onChange={(e) => handleInputChange('lotSizeUnit', e.target.value)}
                >
                  <MenuItem value="sqft">Square Feet</MenuItem>
                  <MenuItem value="acres">Acres</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Financial Information Section */}
          <Typography variant="h6" gutterBottom sx={{ color: '#008080', mt: 4, display: 'flex', alignItems: 'center' }}>
            <AttachMoney sx={{ mr: 1, verticalAlign: 'middle' }} />
            Financial Information
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Asking Price *"
                type="text"
                inputMode="numeric"
                value={formData.askingPrice}
                onChange={(e) => handleInputChange('askingPrice', e.target.value)}
                error={!!errors.askingPrice}
                helperText={errors.askingPrice}
                InputProps={{
                  startAdornment: <AttachMoney sx={{ mr: 1, color: 'action.disabled' }} />,
                }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="HOA Fee (Monthly)"
                type="number"
                value={formData.hoaFee}
                onChange={(e) => handleInputChange('hoaFee', e.target.value)}
                error={!!errors.hoaFee}
                helperText={errors.hoaFee}
                InputProps={{
                  startAdornment: <AttachMoney sx={{ mr: 1, color: 'action.disabled' }} />,
                }}
                placeholder="e.g., 650"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Space Rent (per sq ft/year)"
                type="number"
                value={formData.spaceRent}
                onChange={(e) => handleInputChange('spaceRent', e.target.value)}
                error={!!errors.spaceRent}
                helperText={errors.spaceRent}
                InputProps={{
                  startAdornment: <AttachMoney sx={{ mr: 1, color: 'action.disabled' }} />,
                }}
                placeholder="e.g., 15"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Close of Escrow Date"
                type="date"
                value={formData.closeOfEscrow}
                onChange={(e) => handleInputChange('closeOfEscrow', e.target.value)}
                error={!!errors.closeOfEscrow}
                helperText={errors.closeOfEscrow}
                InputLabelProps={{
                  shrink: true,
                }}
                placeholder="Select closing date"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl component="fieldset">
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Conventional Financing Available
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.conventionalFinancingAvailable}
                      onChange={(e) => handleInputChange('conventionalFinancingAvailable', e.target.checked)}
                      color="primary"
                    />
                  }
                  label={formData.conventionalFinancingAvailable ? "Yes" : "No"}
                />
              </FormControl>
            </Grid>
          </Grid>

          {/* Description Section */}
          <Typography variant="h6" gutterBottom sx={{ color: '#008080', mt: 4, display: 'flex', alignItems: 'center' }}>
            <Description sx={{ mr: 1, verticalAlign: 'middle' }} />
            Property Description
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <TextField
            fullWidth
            multiline
            rows={4}
            label="Description *"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            error={!!errors.description}
            helperText={errors.description}
            placeholder="Describe the property, its features, and investment potential..."
            required
          />

          {/* Property Images Section */}
          <Typography variant="h6" gutterBottom sx={{ color: '#008080', mt: 4, display: 'flex', alignItems: 'center' }}>
            <Photo sx={{ mr: 1, verticalAlign: 'middle' }} />
            Property Images
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" gutterBottom>
                Upload Images
              </Typography>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => handleImageUpload(e)}
                style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
              />
              {formData.uploadedImages && formData.uploadedImages.length > 0 && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {formData.uploadedImages.length} image(s) selected
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" gutterBottom>
                External Image URLs
              </Typography>
              <TextField
                fullWidth
                label="External Image URL"
                value={(formData.externalImageUrls && formData.externalImageUrls[0]) || ''}
                onChange={(e) => {
                  const url = (e.target.value || '').trim();
                  setFormData((prev) => ({
                    ...prev,
                    externalImageUrls: url ? [url] : [],
                  }));
                  if (errors.externalImageUrl) {
                    setErrors((prev) => ({ ...prev, externalImageUrl: undefined }));
                  }
                }}
                onBlur={() => {
                  const raw = formData.externalImageUrls?.[0] || '';
                  if (!raw) return;
                  const normalized = normalizeHttpUrl(raw);
                  if (!normalized) {
                    setErrors((prev) => ({ ...prev, externalImageUrl: 'Please enter a valid URL (https://...)' }));
                    return;
                  }
                  // Normalize on blur so we save consistent URLs.
                  setFormData((prev) => ({ ...prev, externalImageUrls: [normalized] }));
                }}
                placeholder="https://example.com/image.jpg"
                error={Boolean(errors.externalImageUrl)}
                helperText={errors.externalImageUrl || ''}
              />
            </Grid>
          </Grid>

          {/* Property Disclosures Section */}
          <Typography variant="h6" gutterBottom sx={{ color: '#008080', mt: 4, display: 'flex', alignItems: 'center' }}>
            <Description sx={{ mr: 1, verticalAlign: 'middle' }} />
            Property Disclosures
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Typography variant="subtitle2" gutterBottom>
            Upload Disclosure Documents
          </Typography>
          <input
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.txt"
            onChange={(e) => handleDisclosureUpload(e)}
            style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
          />
          {formData.uploadedDisclosures && formData.uploadedDisclosures.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                Uploaded Disclosures:
              </Typography>
              {formData.uploadedDisclosures.map((disclosure, index) => (
                <Chip
                  key={index}
                  label={`${disclosure.name} (${(disclosure.size / 1024 / 1024).toFixed(2)} MB)`}
                  onDelete={() => handleRemoveDisclosure(index)}
                  size="small"
                  sx={{ mr: 1, mb: 1 }}
                />
              ))}
            </Box>
          )}

          {/* iGUIDE/Virtual Tour Section */}
          <Typography variant="h6" gutterBottom sx={{ color: '#008080', mt: 4, display: 'flex', alignItems: 'center' }}>
            🌐 Virtual Tour
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <TextField
            fullWidth
            label="iGUIDE/Virtual Tour URL"
            value={formData.igGuideUrl}
            onChange={(e) => handleInputChange('igGuideUrl', e.target.value)}
            error={!!errors.igGuideUrl}
            helperText={errors.igGuideUrl}
            placeholder="https://myiguide.com/tour/property"
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={handleClose}
          sx={{ color: '#666' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || disclosuresUploading}
          sx={{
            backgroundColor: '#008080',
            '&:hover': { backgroundColor: '#005252' },
            minWidth: 120
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Add Property'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
